import { Fraction } from 'bi-fraction';
import { utils } from 'zksync-ethers';
import { Curve3Pool as Curve3PoolAbi, ERC20 as ERC20Abi, YbEthBridge as YbEthBridgeAbi, YbUsdBridge as YbUsdBridgeAbi, } from '../abis';
import { ETH_DECIMALS, NATIVE_TOKEN_ADDRESS, YB_TOKEN_DECIMALS, } from '../config/constants';
import { ChainId, ChainLayer, FromBalanceTooLowError, YbTokenBridgeTokenType, } from '../types';
import { executeTx, getContract, getProvider, } from '../utils';
import { L2GasEstimator } from './l2GasEstimator';
// TODO not tested
export class YbTokenBridge {
    constructor(options) {
        this.options = options;
        this.getMinAmountReceive = async (amountBigInt, slippage) => {
            // Curve3Pool doesn't support get_dy method on testnet
            if (this.options.l1ProviderOptions.chainId === ChainId.SEPOLIA) {
                return BigInt(10000);
            }
            const isUsdc = this.options.depositTokenAddress === this.options.contractAddressMap.USDC;
            const isUsdt = this.options.depositTokenAddress === this.options.contractAddressMap.USDT;
            // for eth flow: ignored. if it's dai, param will be ignored by YbUsdBridge contract
            if (!isUsdc && !isUsdt)
                return amountBigInt;
            const curve3Pool = getContract(this.options.contractAddressMap.Curve3Pool, Curve3PoolAbi, getProvider(this.options.l1ProviderOptions, ChainLayer.L1));
            const tokenType = isUsdc
                ? YbTokenBridgeTokenType.USDC
                : YbTokenBridgeTokenType.USDT;
            const expectedDy = await curve3Pool.get_dy(tokenType, YbTokenBridgeTokenType.DAI, amountBigInt);
            return new Fraction(expectedDy).mul(Fraction.ONE.sub(slippage)).quotient;
        };
        this.getDepositTokenAddressForGasEstimation = () => {
            if (this.options.depositTokenAddress === NATIVE_TOKEN_ADDRESS) {
                return this.options.contractAddressMap.YbETH;
            }
            return this.options.contractAddressMap.YbUSD;
        };
        this.getL2ReceiverAddress = async (useBot) => {
            if (!useBot)
                return this.options.depositorAddress;
            return this.options.l2BotAddress;
        };
        this.mintAndBridgeEth = (options) => {
            return this.createYbEthBridgeContract().mintAndBridge(this.getL2ReceiverAddress(options.useBot), options.amountBigInt, options.l2GasLimit, utils.REQUIRED_L1_TO_L2_GAS_PER_PUBDATA_LIMIT, this.options.depositorAddress, { value: options.valueForMintAndBridge });
        };
        this.mintAndBridgeUsd = (options) => {
            return this.createYbUsdBridgeContract().mintAndBridge(this.getL2ReceiverAddress(options.useBot), options.amountBigInt, this.options.tokenType, options.minAmountReceive, options.l2GasLimit, utils.REQUIRED_L1_TO_L2_GAS_PER_PUBDATA_LIMIT, this.options.depositorAddress, { value: options.valueForMintAndBridge });
        };
        this.setupDepositTokenContract = () => {
            if (this.isEthBridge()) {
                return {
                    approve: async () => 'noop',
                    allowance: async () => 0n,
                };
            }
            return getContract(this.options.depositTokenAddress, ERC20Abi, this.options.signerOrProvider);
        };
        this.getValueForMintAndBridge = (amountBigInt, l2TxCostInETH) => l2TxCostInETH + (this.isEthBridge() ? amountBigInt : 0n);
        this.isEthBridge = () => this.options.tokenType === YbTokenBridgeTokenType.ETH;
        this.depositTokenContract = this.setupDepositTokenContract();
        this.l2GasEstimator = new L2GasEstimator(options.l1ProviderOptions, options.l2ProviderOptions, options.contractAddressMap, options.signerOrProvider);
    }
    /**
     * Deposits the given amount of tokens to L2 via the YbTokenBridge (YbEthBridge or YbUsdBridge) contract.
     * Will execute approval if needed.
     *
     * @param amount amount to be deposited
     * @returns receipt of the deposit transaction
     */
    async depositToken({ amount, slippage, useBot = false, }) {
        const amountBigInt = amount.shl(this.options.depositTokenDecimals).quotient;
        await this.approveDepositTokenIfNecessary(amountBigInt);
        const [{ l2TxCostInETH, l2GasLimit }, minAmountReceive, ethBalance] = await Promise.all([
            this.getL2GasEstimate(amount),
            this.getMinAmountReceive(amountBigInt, slippage),
            getProvider(this.options.l1ProviderOptions, ChainLayer.L1).getBalance(this.options.depositorAddress),
        ]);
        const valueForMintAndBridge = this.getValueForMintAndBridge(amountBigInt, l2TxCostInETH);
        if (valueForMintAndBridge > ethBalance) {
            throw new FromBalanceTooLowError(new Fraction(valueForMintAndBridge - ethBalance).shr(ETH_DECIMALS));
        }
        let tx;
        if (this.isEthBridge()) {
            tx = this.mintAndBridgeEth({
                useBot,
                amountBigInt,
                l2GasLimit,
                valueForMintAndBridge,
            });
        }
        else {
            tx = this.mintAndBridgeUsd({
                useBot,
                amountBigInt,
                l2GasLimit,
                valueForMintAndBridge,
                minAmountReceive,
            });
        }
        return await executeTx(tx);
    }
    async getL2GasEstimate(amount) {
        const l2GasEstimate = await this.l2GasEstimator.getL2GasEstimate({
            depositorAddress: this.options.depositorAddress,
            depositTokenAddress: this.getDepositTokenAddressForGasEstimation(),
            amount,
            decimals: BigInt(YB_TOKEN_DECIMALS),
        });
        return l2GasEstimate;
    }
    async approveDepositTokenIfNecessary(amount) {
        const allowance = await this.depositTokenContract.allowance(this.options.depositorAddress, this.options.bridgeAddress);
        if (allowance >= amount)
            return;
        const approveTx = await this.depositTokenContract.approve(this.options.bridgeAddress, amount);
        if (approveTx === 'noop')
            return;
        await approveTx.wait();
    }
    createYbEthBridgeContract() {
        return getContract(this.options.contractAddressMap.YbETHBridge, YbEthBridgeAbi, this.options.signerOrProvider);
    }
    createYbUsdBridgeContract() {
        return getContract(this.options.contractAddressMap.YbUSDBridge, YbUsdBridgeAbi, this.options.signerOrProvider);
    }
}
